import { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useRequest from 'use-request';
import { EditorProvider, Editor, Toolbar, BtnBold, BtnNumberedList, BtnBulletList } from 'react-simple-wysiwyg';

import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Modal,
  FormHelperText,
  IconButton,
  Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import copy from 'copy-to-clipboard';

// Utils
import messages from '../../static/messages';
import { AuthContext } from '../../context/AuthProvider';
import { formatPhone } from '../../utils/formatter';

// Services
import proposalsServices from '../../services/proposalsServices';
import clientsServices from '../../services/clientsServices';
import publicServices from '../../services/publicServices';
import smsServices from '../../services/smsServices';

// Styles
const modal_style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 500, md: 600 },
  maxWidth: '95vw',
  maxHeight: '90vh',
  overflow: 'auto',
  backgroundColor: '#fff',
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  borderRadius: 2,
};

/**
 * @description Get the base URL for proposal links
 * @param {object} companyData - The company data containing publicUrl
 * @returns {string} - The base URL for proposal links
 */
function getProposalBaseUrl(companyData) {
  // Use publicUrl from company settings if available
  if (companyData?.publicUrl) {
    // Remove trailing slash if present
    return companyData.publicUrl.replace(/\/$/, '');
  }
  
  // Fallback to hostname-based detection
  const isStaging = window.location.hostname === 'localhost' || window.location.hostname === 'www.staging.airmaxxpro.com';
  return `https://www.${isStaging ? 'staging' : 'app'}.airmaxxpro.com`;
}

/**
 * @description Email template for the proposal
 * @param {string} id - The id of the proposal
 * @param {object} data - The data of the proposal
 * @param {string} token - The token of the client
 * @param {string} name - The name of the user
 * @param {string} title - The title of the user
 * @param {object} companyData - The company data of the user
 * @returns {{ subject: string, content: string }} - The email template
 */
function email_template (id, data, token, userData, companyData) {
  const baseUrl = getProposalBaseUrl(companyData);
  // Find selected client by selectedClientId, fallback to first client
  const selectedClientProperty = data?.selectedClientId 
    ? data?.property?.clients?.find(c => c.client?.id === data.selectedClientId)
    : data?.property?.clients?.[0];
  const client = selectedClientProperty?.client;
  const clientName = client?.firstName || (client?.fullName ? client.fullName.split(" ")[0] : "");
  const userName = userData?.fullName || '';
  const userPhone = userData?.phoneNumber || '';
  const userEmail = userData?.email || '';
  return {
    subject: 'Airmaxx Estimate',
    content: `
      <p>Hi${clientName ? ` ${clientName}` : ''},</p>

      <p>Please follow the link below to view your home improvement contract:</p>

      <a href="${baseUrl}/proposals/view/${id}?token=${token}">Your Home Improvement Contract</a>

      <p>
        We have carefully outlined the recommended solutions to ensure optimal efficiency, reliability, and comfort. If
        you have any questions or would like to discuss the details further, feel free to reach out. We're happy to
        assist you and tailor our services to best suit your needs.
      </p>

      <p>Best regards,<br />${userName}</p>

      <table
        cellpadding="0"
        cellspacing="0"
        style="width: 420px; vertical-align: -webkit-baseline-middle; font-size: medium; font-family: Arial; margin-top: 30px;"
      >
        <tbody>
          <tr>
            <td
              color="#0d3b59"
              direction="horizontal"
              width="auto"
              height="1"
              style="width: 100%; border-bottom: 1px solid rgb(13, 59, 89); border-left: none; display: block"
            ></td>
          </tr>
          <tr>
            <td>
              <table
                cellpadding="0"
                cellspacing="0"
                style="vertical-align: -webkit-baseline-middle; font-size: medium; font-family: Arial"
              >
                <tbody>
                  ${userPhone ? `<tr height="25" style="vertical-align: middle">
                    <td width="30" style="vertical-align: middle">
                      <table
                        cellpadding="0"
                        cellspacing="0"
                        style="vertical-align: -webkit-baseline-middle; font-size: medium; font-family: Arial"
                      >
                        <tbody>
                          <tr>
                            <td style="vertical-align: bottom">
                              <span
                                color="#0d3b59"
                                width="11"
                                style="display: inline-block; background-color: rgb(13, 59, 89)"
                                ><img
                                  src="https://cdn2.hubspot.net/hubfs/53/tools/email-signature-generator/icons/phone-icon-2x.png"
                                  color="#0d3b59"
                                  alt="mobilePhone"
                                  width="13"
                                  class="contact-info__ContactLabelIcon-sc-mmkjr6-0 dGVIJx"
                                  style="display: block; background-color: rgb(13, 59, 89)"
                              /></span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                    <td style="padding: 0px; color: rgb(0, 0, 0); vertical-align: bottom">
                      <a
                        href="tel:${userPhone}"
                        color="#000000"
                        style="text-decoration: none; color: rgb(0, 0, 0); font-size: 12px"
                        ><span>${formatPhone(userPhone)}</span></a
                      >
                    </td>
                  </tr>` : ''}
                  ${userEmail ? `<tr height="25" style="vertical-align: middle">
                    <td width="30" style="vertical-align: middle">
                      <table
                        cellpadding="0"
                        cellspacing="0"
                        style="vertical-align: -webkit-baseline-middle; font-size: medium; font-family: Arial"
                      >
                        <tbody>
                          <tr>
                            <td style="vertical-align: bottom">
                              <span
                                color="#0d3b59"
                                width="11"
                                style="display: inline-block; background-color: rgb(13, 59, 89)"
                                ><img
                                  src="https://cdn2.hubspot.net/hubfs/53/tools/email-signature-generator/icons/email-icon-2x.png"
                                  color="#0d3b59"
                                  alt="emailAddress"
                                  width="13"
                                  class="contact-info__ContactLabelIcon-sc-mmkjr6-0 dGVIJx"
                                  style="display: block; background-color: rgb(13, 59, 89)"
                              /></span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                    <td style="padding: 0px; vertical-align: bottom">
                      <a
                        href="mailto:${userEmail}"
                        color="#000000"
                        style="text-decoration: none; color: rgb(0, 0, 0); font-size: 12px"
                        ><span>${userEmail}</span></a
                      >
                    </td>
                  </tr>` : ''}
                  <tr height="25" style="vertical-align: middle">
                    <td width="30" style="vertical-align: middle">
                      <table
                        cellpadding="0"
                        cellspacing="0"
                        style="vertical-align: -webkit-baseline-middle; font-size: medium; font-family: Arial"
                      >
                        <tbody>
                          <tr>
                            <td style="vertical-align: bottom">
                              <span
                                color="#0d3b59"
                                width="11"
                                style="display: inline-block; background-color: rgb(13, 59, 89)"
                                ><img
                                  src="https://cdn2.hubspot.net/hubfs/53/tools/email-signature-generator/icons/link-icon-2x.png"
                                  color="#0d3b59"
                                  alt="website"
                                  width="13"
                                  style="display: block; background-color: rgb(13, 59, 89)"
                              /></span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                    <td style="padding: 0px; vertical-align: bottom">
                      <a
                        href="https://airmaxx.com"
                        target="_blank"
                        color="#000000"
                        style="text-decoration: none; color: rgb(0, 0, 0); font-size: 12px"
                        ><span>airmaxx.com</span></a
                      >
                    </td>
                  </tr>
                  ${companyData.address ? `<tr height="25" style="vertical-align: middle">
                    <td width="30" style="vertical-align: middle">
                      <table
                        cellpadding="0"
                        cellspacing="0"
                        style="vertical-align: -webkit-baseline-middle; font-size: medium; font-family: Arial"
                      >
                        <tbody>
                          <tr>
                            <td style="vertical-align: bottom">
                              <span
                                color="#0d3b59"
                                width="11"
                                style="display: inline-block; background-color: rgb(13, 59, 89)"
                                ><img
                                  src="https://cdn2.hubspot.net/hubfs/53/tools/email-signature-generator/icons/address-icon-2x.png"
                                  color="#0d3b59"
                                  alt="address"
                                  width="13"
                                  style="display: block; background-color: rgb(13, 59, 89)"
                              /></span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                    <td style="padding: 0px; vertical-align: bottom" colspan="2">
                      <span color="#000000" style="font-size: 12px; color: rgb(0, 0, 0)"
                        ><span>${companyData.address}</span></span
                      >
                    </td>
                  </tr>` : ''}
                </tbody>
              </table>
            </td>
          </tr>

          <tr>
            <td
              color="#0d3b59"
              direction="horizontal"
              width="auto"
              height="1"
              style="width: 100%; border-bottom: 1px solid rgb(13, 59, 89); border-left: none; display: block; height: 20px !important"
            ></td>
          </tr>
          <tr>
            <td style="padding-top: 20px; text-align: center;">
              <img 
                src="https://airmaxxapp.com/images/Airmaxx-Logo-Trans.png" 
                alt="Airmaxx" 
                style="max-height: 50px; max-width: 150px;"
              />
            </td>
          </tr>
        </tbody>
      </table>
    `,
  };
}

const required_fields = ['emailTo', 'subject', 'content'];

const EmailProposalModal = ({ isOpen, onClose, data }) => {
  const { id } = useParams();
  const { userData } = useContext(AuthContext);

  const [email, setEmail] = useState();
  const [errors, setErrors] = useState({});
  const [isReady, setReady] = useState(false);
  const [proposalLink, setProposalLink] = useState('');
  const [saveEmail, setSaveEmail] = useState(true);
  const [selectedClientForSave, setSelectedClientForSave] = useState('');
  
  // Get clients from property
  const propertyClients = data?.property?.clients?.map(cp => cp.client).filter(Boolean) || [];

  const publicSettingsRequest = useRequest(() => publicServices.getPublicSettings().then((response) => response.data.value));

  // Action functions
  const resetErrors = () => setErrors(required_fields.reduce((acc, el) => ({ ...acc, [el]: false }), {}));

  // Handler functions
  const handleChange = (event) => {
    const key = event.target.name;
    const val = event.target.value;

    setEmail((prevState) => ({
      ...prevState,
      [key]: val,
    }));
  };

  const onChangeTemplate = (event) => setEmail((prevState) => ({ ...prevState, content: event.target.value }));

  const handleValidate = () => {
    resetErrors();

    required_fields.forEach((field) => {
      if (email[field] === '' || !email[field]) {
        setErrors((prevState) => ({
          ...prevState,
          [field]: true,
        }));
        setReady(false);
      } else {
        setErrors((prevState) => ({
          ...prevState,
          [field]: false,
        }));
        setReady(true);
      }
    });
  };

  // Copy link to clipboard
  const handleCopyLink = () => {
    if (!proposalLink) {
      toast.error('Link not available yet');
      return;
    }
    
    const success = copy(proposalLink, {
      format: 'text/plain'
    });
    
    if (success) {
      toast.success('Link copied to clipboard!');
    } else {
      toast.error('Failed to copy. Please select the link and copy manually (Ctrl+C)');
    }
  };

  // Async functions
  const getClientToken = async () => {
    // Use selectedClientId if available, fallback to first client
    const selectedClientProperty = data?.selectedClientId 
      ? data?.property?.clients?.find(c => c.client?.id === data.selectedClientId)
      : data?.property?.clients?.[0];
    const clientId = selectedClientProperty?.client?.id;

    if (!clientId) {
      toast.error('No client found for this property');
      return;
    }

    try {
      // Get client token and company data in parallel
      const [tokenResponse, companyDataResponse] = await Promise.all([
        clientsServices.getClientToken(clientId),
        publicSettingsRequest.value || publicSettingsRequest.execute(),
      ]);

      const companyData = companyDataResponse || {};

      if (tokenResponse.status === 200) {
        const token = tokenResponse.data.token;
        const baseUrl = getProposalBaseUrl(companyData);
        const selectedClientEmail = selectedClientProperty?.client?.email;

        // Same full link for both email body and copy field (allows signing)
        const link = `${baseUrl}/proposals/view/${id}?token=${token}`;
        setProposalLink(link);

        setEmail({
          ...email_template(id, data, token, userData, companyData),
          emailTo: selectedClientEmail || '',
        });
      }
    } catch (error) {
      toast.error(messages.errors.error_data_loaning);
      console.log(error);
    }
  };

  const sendEmail = async () => {
    const emailData = {
      ...email,
      emailTo: email.emailTo.split(',').map((el) => el.trim()),
    };

    try {
      // Save email to client profile if checkbox is checked
      if (saveEmail && selectedClientForSave && email?.emailTo) {
        const emailToSave = email.emailTo.split(',')[0].trim(); // Take first email if multiple
        try {
          await clientsServices.update(selectedClientForSave, { email: emailToSave });
          toast.success('Email saved to client profile');
        } catch (saveError) {
          console.error('Failed to save email to client:', saveError);
          toast.warning('Email sent but failed to save to client profile');
        }
      }

      const response = await proposalsServices.sendEmail(id, emailData);

      if (response.status === 200) {
        onClose();
        toast.success('Email sent successfully');

        // Send SMS notification to the client's phone (fire and forget)
        try {
          const smsClientId = selectedClientForSave || data?.selectedClientId;
          const smsClientProperty = smsClientId
            ? data?.property?.clients?.find(c => c.client?.id === smsClientId)
            : data?.property?.clients?.[0];
          const client = smsClientProperty?.client;

          if (client?.phoneNumber) {
            const smsResponse = await smsServices.sendProposalNotification({
              proposalId: id,
              clientPhone: client.phoneNumber,
              clientName: client.firstName || client.fullName?.split(' ')[0] || '',
            });

            if (smsResponse.data?.success) {
              toast.info('SMS notification also sent to client');
            }
          }
        } catch (smsError) {
          console.warn('SMS notification failed (email was sent successfully):', smsError);
        }
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  // Hooks
  useEffect(() => {
    getClientToken();

    required_fields.forEach((el) =>
      setErrors((prevState) => ({
        ...prevState,
        [el]: false,
      })),
    );
    
    // Set default selected client for save email
    if (propertyClients.length > 0) {
      // Use selectedClientId from proposal or first client
      const defaultClient = data?.selectedClientId 
        ? propertyClients.find(c => c.id === data.selectedClientId)
        : propertyClients[0];
      if (defaultClient) {
        setSelectedClientForSave(defaultClient.id);
      }
    }
  }, [data]);

  useEffect(() => {
    if (isReady) !Object.values(errors).includes(true) && sendEmail();
  }, [errors]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box sx={modal_style}>
        <Typography variant="h5" sx={{ textAlign: 'center' }}>
          Email this proposal
        </Typography>

        <TextField
          required
          name="emailTo"
          label="To"
          value={email?.emailTo || ''}
          fullWidth
          onChange={handleChange}
          error={errors.emailTo}
          helperText={errors.emailTo ? messages.errors.required_field : "Emails separated by commas"}
          sx={{ mt: 4 }}
        />

        {/* Save email to client checkbox */}
        {propertyClients.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={saveEmail}
                  onChange={(e) => setSaveEmail(e.target.checked)}
                  size="small"
                />
              }
              label="Save this email"
              sx={{ mr: 0 }}
            />
            {saveEmail && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <Select
                  value={selectedClientForSave}
                  onChange={(e) => setSelectedClientForSave(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: '14px' }}
                >
                  {propertyClients.map((client) => (
                    <MenuItem key={client.id} value={client.id}>
                      {client.fullName || `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'Unknown'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}

        <TextField
          required
          name="subject"
          label="Subject"
          value={email?.subject}
          fullWidth
          autoFocus
          onChange={handleChange}
          error={errors.subject}
          helperText={errors.subject && messages.errors.required_field}
          sx={{ mt: 4, mb: 4 }}
        />

        <EditorProvider>
          <Editor value={email?.content} onChange={onChangeTemplate} style={{ border: '1px solid red !important' }}>
            <Toolbar>
              <BtnBold />
              <BtnBulletList />
              <BtnNumberedList />
            </Toolbar>
          </Editor>
        </EditorProvider>
        {errors.content && <FormHelperText error>{messages.errors.required_field}</FormHelperText>}

        {/* Public Link Section */}
        {proposalLink && (
          <Box sx={{ mt: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
              Public Link (select and copy):
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                value={proposalLink}
                size="small"
                fullWidth
                InputProps={{
                  readOnly: true,
                  sx: { fontSize: '12px', backgroundColor: '#fff' }
                }}
                onClick={(e) => e.target.select()}
              />
              <Tooltip title="Copy to clipboard">
                <IconButton 
                  color="primary" 
                  onClick={handleCopyLink}
                  size="small"
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="contained" color="error" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleValidate}>
            Send
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default EmailProposalModal;
